import React, { useState, useEffect } from 'react';
import { Check, X, Crown, Star, Lock } from 'lucide-react';
import { subscriptionsAPI } from '../api';
import useAuthStore from '../store/authStore';
import toast from 'react-hot-toast';
import { useTheme } from '../context/ThemeContext';

// Mirrors backend/models/Subscription.js PLAN_LIMITS and CLAUDE.md's documented
// pricing exactly — this page previously showed invented plan names and prices
// (2,000/4,000/8,000 EGP) that had nothing to do with what the backend actually
// enforces or charges.
const plans = [
  {
    id: 'free',
    name: 'مجانية',
    nameEn: 'Free',
    price: 0,
    priceYearly: 0,
    features: [
      { text: 'قناة واحدة', included: true },
      { text: '100 محادثة شهرياً', included: true },
      { text: '1000 رسالة شهرياً', included: true },
      { text: 'عضوان في الفريق', included: true },
      { text: '50 رد بالذكاء الاصطناعي شهرياً', included: true },
      { text: 'ردود تلقائية', included: true },
      { text: 'قوالب مخصصة', included: false },
      { text: 'تقارير متقدمة', included: false }
    ]
  },
  {
    id: 'basic',
    name: 'أساسية',
    nameEn: 'Basic',
    price: 299,
    priceYearly: 2990,
    popular: true,
    features: [
      { text: '3 قنوات', included: true },
      { text: '1000 محادثة شهرياً', included: true },
      { text: '10000 رسالة شهرياً', included: true },
      { text: '5 أعضاء فريق', included: true },
      { text: '500 رد بالذكاء الاصطناعي شهرياً', included: true },
      { text: 'قوالب مخصصة', included: true },
      { text: '14 يوم تجربة مجانية', included: true },
      { text: 'تقارير متقدمة', included: false }
    ]
  },
  {
    id: 'standard',
    name: 'قياسية',
    nameEn: 'Standard',
    price: 599,
    priceYearly: 5990,
    features: [
      { text: '5 قنوات', included: true },
      { text: '5000 محادثة شهرياً', included: true },
      { text: '50000 رسالة شهرياً', included: true },
      { text: '10 أعضاء فريق', included: true },
      { text: '2000 رد بالذكاء الاصطناعي شهرياً', included: true },
      { text: 'قوالب مخصصة', included: true },
      { text: 'دعم على مدار الساعة', included: true },
      { text: 'تقارير متقدمة', included: true }
    ]
  },
  {
    id: 'premium',
    name: 'مميزة',
    nameEn: 'Premium',
    price: 999,
    priceYearly: 9990,
    features: [
      { text: '8 قنوات (جميع القنوات)', included: true },
      { text: 'محادثات غير محدودة', included: true },
      { text: 'رسائل غير محدودة', included: true },
      { text: 'فريق غير محدود', included: true },
      { text: 'ردود ذكاء اصطناعي غير محدودة', included: true },
      { text: 'دعم VIP', included: true },
      { text: 'تقارير متقدمة', included: true },
      { text: 'API مخصص', included: true }
    ]
  }
];

const Subscription = () => {
  const theme = useTheme();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currentPlan, setCurrentPlan] = useState('free');
  const [loading, setLoading] = useState(true);
  const { user } = useAuthStore();

  useEffect(() => {
    fetchCurrentSubscription();
  }, []);

  const fetchCurrentSubscription = async () => {
    try {
      const { data } = await subscriptionsAPI.getCurrent();
      setCurrentPlan(data.subscription.plan);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    toast('سيتم إضافة الدفع الإلكتروني قريباً! 🔜', { icon: '💳' });
  };

  const getPrice = (plan) => {
    const price = billingCycle === 'yearly' ? plan.priceYearly : plan.price;
    return price;
  };

  const getSavings = (plan) => {
    if (billingCycle === 'yearly') {
      return Math.round(plan.price * 12 - plan.priceYearly);
    }
    return 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-2">خطط الاشتراك</h1>
        <p className={`text-slate-600 ${theme === 'light' ? '' : 'text-slate-400'}`}>اختر الخطة المناسبة لاحتياجاتك</p>
        
        {/* Free Plan Badge */}
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold mt-4 ${theme === 'light' ? 'bg-sky-50 text-sky-700' : 'bg-sky-500/20 text-sky-400'}`}>
          ✨ خطة مجانية متاحة، بدون بطاقة ائتمان
        </div>
      </div>

      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={() => setBillingCycle('monthly')}
          className={`px-6 py-2 rounded-lg transition-colors ${
            billingCycle === 'monthly'
              ? 'bg-sky-600 text-white'
              : (theme === 'light' ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-dark-700 text-slate-300 hover:bg-dark-600')
          }`}
        >
          شهري
        </button>
        <button
          onClick={() => setBillingCycle('yearly')}
          className={`px-6 py-2 rounded-lg transition-colors ${
            billingCycle === 'yearly'
              ? 'bg-sky-600 text-white'
              : (theme === 'light' ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-dark-700 text-slate-300 hover:bg-dark-600')
          }`}
        >
          سنوي
          <span className={`mr-2 text-xs px-2 py-0.5 rounded-full ${theme === 'light' ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/20 text-emerald-400'}`}>
            وفر شهرين
          </span>
        </button>
      </div>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan) => {
          const isCurrentPlan = currentPlan === plan.id;
          const price = getPrice(plan);
          const savings = getSavings(plan);

          return (
            <div
              key={plan.id}
              className={`card p-6 relative ${
                plan.popular ? 'border-sky-300 border-2' : ''
              } ${isCurrentPlan ? 'ring-2 ring-sky-500' : ''}`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-3 right-1/2 translate-x-1/2 bg-sky-600 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  الأكثر شعبية
                </div>
              )}

              {/* Current Plan Badge */}
              {isCurrentPlan && (
                <div className="absolute -top-3 right-1/2 translate-x-1/2 bg-green-500 text-white text-xs px-3 py-1 rounded-full">
                  خطتك الحالية
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold">{plan.name}</h3>
                <p className={`text-xs ${theme === 'light' ? 'text-slate-600' : 'text-slate-400'}`}>{plan.nameEn}</p>
                
                <div className="mt-4">
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-4xl font-black">{price.toLocaleString()}</span>
                    <span className={`text-lg ${theme === 'light' ? 'text-slate-600' : 'text-slate-400'}`}>جنيهاً</span>
                  </div>
                  <p className={`text-sm ${theme === 'light' ? 'text-slate-600' : 'text-slate-400'}`}>
                    / {billingCycle === 'yearly' ? 'سنوياً' : 'شهرياً'}
                  </p>
                  {savings > 0 && (
                    <p className="text-xs text-emerald-600 mt-1">
                      توفير {savings.toLocaleString()} جنيه
                    </p>
                  )}
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm">
                    {feature.included ? (
                      <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    )}
                    <span className={feature.included ? (theme === 'light' ? 'text-slate-700' : 'text-slate-300') : 'text-slate-400'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Action Button */}
              <button
                onClick={() => handleUpgrade(plan.id)}
                disabled={isCurrentPlan}
                className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                  isCurrentPlan
                    ? (theme === 'light' ? 'bg-slate-200 text-slate-600 cursor-not-allowed' : 'bg-dark-700 text-slate-400 cursor-not-allowed')
                    : plan.popular
                    ? 'bg-sky-600 hover:bg-sky-700 text-white'
                    : 'btn-secondary'
                }`}
              >
                {isCurrentPlan ? 'خطتك الحالية' : 'ابدأ الآن'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Features Comparison */}
      <div className="card p-6">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="font-bold">لماذا تختار هندل؟</h3>
          <span className={`px-2 py-1 text-xs rounded-full ${theme === 'light' ? 'bg-sky-50 text-sky-700' : 'bg-sky-500/20 text-sky-400'}`}>
            واتس آب أولاً
          </span>
        </div>
        <div className={`grid md:grid-cols-3 gap-4 text-sm ${theme === 'light' ? 'text-slate-600' : 'text-slate-400'}`}>
          <div className={`p-4 rounded-xl border ${theme === 'light' ? 'bg-slate-50 border-slate-200 text-slate-700' : 'bg-dark-800 border-dark-600 text-slate-300'}`}>
            <p className={`font-semibold mb-2 ${theme === 'light' ? 'text-slate-900' : 'text-white'}`}>📱 توصيل سريع</p>
            <p>وصّل واتس آب في دقائق عبر QR أو API</p>
          </div>
          <div className={`p-4 rounded-xl border ${theme === 'light' ? 'bg-slate-50 border-slate-200 text-slate-700' : 'bg-dark-800 border-dark-600 text-slate-300'}`}>
            <p className={`font-semibold mb-2 ${theme === 'light' ? 'text-slate-900' : 'text-white'}`}>🤖 ردود ذكية</p>
            <p>ردود تلقائية بالعربي مع دعم AI</p>
          </div>
          <div className={`p-4 rounded-xl border ${theme === 'light' ? 'bg-slate-50 border-slate-200 text-slate-700' : 'bg-dark-800 border-dark-600 text-slate-300'}`}>
            <p className={`font-semibold mb-2 ${theme === 'light' ? 'text-slate-900' : 'text-white'}`}>🔒 آمن وموثوق</p>
            <p>تشفير كامل لحماية بيانات عملائك</p>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="card p-6">
        <h3 className={`font-bold mb-4 ${theme === 'light' ? 'text-slate-900' : 'text-white'}`}>أسئلة شائعة</h3>
        <div className={`space-y-4 text-sm ${theme === 'light' ? 'text-slate-600' : 'text-slate-400'}`}>
          <div>
            <p className={`font-semibold ${theme === 'light' ? 'text-slate-900' : 'text-white'}`}>هل يمكنني تغيير الخطة لاحقاً؟</p>
            <p>نعم، يمكنك الترقية أو الإلغاء في أي وقت من إعدادات الحساب.</p>
          </div>
          <div>
            <p className={`font-semibold ${theme === 'light' ? 'text-slate-900' : 'text-white'}`}>ما هي طرق الدفع المتاحة؟</p>
            <p>فودافون كاش وفوري.</p>
          </div>
          <div>
            <p className={`font-semibold ${theme === 'light' ? 'text-slate-900' : 'text-white'}`}>هل هناك فترة تجريبية؟</p>
            <p>نعم، جميع الخطط الجديدة تحصل على 14 يوم تجربة مجانية.</p>
          </div>
          <div>
            <p className={`font-semibold ${theme === 'light' ? 'text-slate-900' : 'text-white'}`}>ما هي القنوات المتاحة؟</p>
            <p>حالياً واتس آب فقط. قنوات أخرى (ماسنجر، إنستجرام، تيليجرام) قريباً!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;